/**
 * Client project links.
 *
 * Every project has one permanent, shareable URL — portalproductions.me/project/
 * <slug> — derived from the client and project name. Everyone on the client
 * team uses the same link; the slug never changes once issued.
 */

export const CLIENT_LINK_PREFIX = '/project/'

/** "Suited & Booted — Podcast" → "suited-and-booted-podcast". */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/&/g, ' and ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** A slug for this client and project that no existing project is using. */
export function uniqueSlug(client: string, name: string, taken: string[]): string {
  const base = slugify(`${client} ${name}`) || 'project'
  if (!taken.includes(base)) return base

  let counter = 2
  while (taken.includes(`${base}-${counter}`)) counter += 1
  return `${base}-${counter}`
}

/** The path this prototype navigates to, e.g. "/project/atm-tv". */
export function clientPath(slug: string): string {
  return `${CLIENT_LINK_PREFIX}${slug}`
}

/**
 * The link Portal staff send to a client — always on whatever domain the app
 * is currently served from, so connecting a custom domain needs no code change.
 *
 * The access token is part of the link, not an optional extra: without one
 * there is no link to give out, so this returns '' rather than a slug-only URL
 * that would fail at join_project.
 */
export function clientLink(slug: string, accessToken: string): string {
  if (!slug || !accessToken) return ''
  const origin =
    typeof window !== 'undefined' && window.location.origin ? window.location.origin : ''
  return `${origin}${clientPath(slug)}?access=${encodeURIComponent(accessToken)}`
}

/** The slug in a path, or '' when the path is not a client project link. */
export function slugFromPath(path: string): string {
  if (!path.startsWith(CLIENT_LINK_PREFIX)) return ''
  return path.slice(CLIENT_LINK_PREFIX.length).split('/')[0] ?? ''
}
